
import React, { useState, useRef, useEffect } from 'react';
import { ArrowRight, FileText, Link as LinkIcon, Download, Zap, ShieldCheck, Clock, Activity, Check, Lock, Database, Settings, FileType, AlignLeft, CheckCircle2, MoreHorizontal, FileJson, PenTool, Bold, Italic, Type, Fingerprint, Eye, Mic, Play, Pause, FileCode, File, Sparkles } from 'lucide-react';
import { useLanguage } from '../i18nContext';

interface LandingPageProps {
    onStart: () => void;
    onLogin: () => void;
    onNavigate?: (page: string) => void;
}

export default function LandingPage({ onStart, onNavigate }: LandingPageProps) {
    const { t, language } = useLanguage();

    // Complex ROI State
    const [teamSize, setTeamSize] = useState(21);
    const [meetingsPerWeek, setMeetingsPerWeek] = useState(8);
    const [hourlyRate, setHourlyRate] = useState(60); // dollars

    // ROI Logic
    const hoursSavedPerYear = Math.round(teamSize * meetingsPerWeek * 0.75 * 52);
    const moneySaved = Math.round(hoursSavedPerYear * hourlyRate);

    // Derived animation values based on input
    const totalActivity = teamSize + meetingsPerWeek + (hourlyRate / 10);
    const rotationDeg = totalActivity * 8; // Faster rotation
    const fidelityPercent = Math.min(85 + (totalActivity / 5), 100);

    // Connection Line Refs
    const containerRef = useRef<HTMLDivElement>(null);
    const sourceRef = useRef<HTMLDivElement>(null);
    const targetRef = useRef<HTMLDivElement>(null);
    const [connectionPath, setConnectionPath] = useState("");

    useEffect(() => {
        const updateConnection = () => {
            if (containerRef.current && sourceRef.current && targetRef.current) {
                const containerRect = containerRef.current.getBoundingClientRect();
                const sourceRect = sourceRef.current.getBoundingClientRect();
                const targetRect = targetRef.current.getBoundingClientRect();

                // Calculate center points of the dots relative to the container
                const startX = sourceRect.left + (sourceRect.width / 2) - containerRect.left;
                const startY = sourceRect.top + (sourceRect.height / 2) - containerRect.top;

                const endX = targetRect.left + (targetRect.width / 2) - containerRect.left;
                const endY = targetRect.top + (targetRect.height / 2) - containerRect.top;

                // Control points for smooth S-curve
                const cp1X = startX + (endX - startX) * 0.5;
                const cp1Y = startY;
                const cp2X = endX - (endX - startX) * 0.5;
                const cp2Y = endY;

                setConnectionPath(`M ${startX} ${startY} C ${cp1X} ${cp1Y}, ${cp2X} ${cp2Y}, ${endX} ${endY}`);
            }
        };

        // Initial calculation
        updateConnection();

        // Listen for resize and scroll
        window.addEventListener('resize', updateConnection);
        window.addEventListener('scroll', updateConnection, true);

        // Add a small delay to ensure layout is settled
        const timer = setTimeout(updateConnection, 100);

        return () => {
            window.removeEventListener('resize', updateConnection);
            window.removeEventListener('scroll', updateConnection, true);
            clearTimeout(timer);
        };
    }, []); // Run once on mount

    // Helper for retro sliders - UPDATED COLORS (Green/Gold/Brown) per screenshot
    const getSliderStyle = (value: number, min: number, max: number, type: 'green' | 'gold' | 'rust') => {
        const percent = ((value - min) / (max - min)) * 100;

        let startColor, endColor;

        if (type === 'green') {
            // Muted Sage to Grass Green
            startColor = '#D9E2C8';
            endColor = '#84cc16';
        } else if (type === 'gold') {
            // Champagne to Gold
            startColor = '#FDF0D5';
            endColor = '#F5E0B7';
        } else {
            // Muted Brown to Rust
            startColor = '#d1ccc4';
            endColor = '#C1A186';
        }

        // Dark track background (darker brown)
        const trackColor = '#3e3430';

        return {
            background: `linear-gradient(to right, ${startColor} 0%, ${endColor} ${percent}%, ${trackColor} ${percent}%, ${trackColor} 100%)`
        };
    };

    return (
        <div className="w-full flex flex-col items-center bg-surface">

            {/* Hero Section */}
            <section className="w-full max-w-7xl mx-auto px-6 pt-24 pb-20 flex flex-col items-center text-center">
                {/* Pill Label */}
                <div className="mb-6 px-4 py-1.5 rounded-full bg-surface-border border border-surface-divider text-xs font-bold text-brand-800 uppercase tracking-wide flex items-center gap-2 shadow-sm">
                    <span className="w-2 h-2 rounded-full bg-brand-800 animate-pulse"></span>
                    <span className="font-sans font-bold text-brand-900 tracking-wider">{t.landing.pill}</span>
                </div>
                <h1 className="text-5xl md:text-7xl font-serif font-bold tracking-tight text-brand-900 leading-[1.05] mb-8 max-w-5xl">
                    {t.landing.heroTitle}
                </h1>
                <p className="text-xl text-brand-700 max-w-2xl mb-12 font-light leading-relaxed">
                    {t.landing.heroSubtitle}
                </p>

                {/* Main CTA - Brown Theme */}
                <button
                    onClick={onStart}
                    className="group relative px-10 py-4 rounded-full bg-ink-dark hover:bg-prestige-900 text-surface-light shadow-[0_10px_20px_-5px_rgba(92,77,71,0.4)] hover:shadow-[0_15px_30px_-5px_rgba(92,77,71,0.5)] hover:-translate-y-1 transition-all duration-300 overflow-hidden"
                >
                    {/* Subtle Matte Noise */}
                    <div className="absolute inset-0 bg-noise opacity-10 mix-blend-overlay"></div>

                    <div className="relative z-10 flex items-center gap-3 text-lg font-bold tracking-wide text-surface-light drop-shadow-sm">
                        {t.landing.cta}
                        <ArrowRight className="w-5 h-5 text-white group-hover:translate-x-1 transition-transform stroke-[2.5]" />
                    </div>
                </button>

                {/* High-Fidelity Chaos -> Structure Visualization */}
                <div className="mt-24 relative w-full max-w-6xl mx-auto perspective-[2000px] group text-left">
                    {/* Window Chrome - Updated Colors */}
                    <div className="relative bg-surface-light rounded-card border border-surface-border shadow-2xl overflow-hidden ring-1 ring-ink-dark/5 transform transition-transform duration-700 hover:rotate-x-1">
                        {/* Chrome Header */}
                        <div className="h-12 bg-surface border-b border-surface-hover flex items-center justify-between px-4 z-20 relative">
                            {/* Traffic Lights */}
                            <div className="flex gap-2">
                                <div className="w-3 h-3 rounded-full bg-gray-300"></div>
                                <div className="w-3 h-3 rounded-full bg-gray-300"></div>
                                <div className="w-3 h-3 rounded-full bg-gray-300"></div>
                            </div>
                            <div className="flex items-center gap-2 px-3 py-1 bg-white rounded-md border border-surface-hover shadow-sm">
                                <Lock size={10} className="text-ink-secondary" />
                                <span className="text-[10px] font-semibold text-ink-secondary">{t.landing.mockup.windowTitle}</span>
                            </div>
                            <div className="w-10"></div>
                        </div>

                        {/* Main Content Area */}
                        <div ref={containerRef} className="flex h-[550px] relative bg-white">

                            {/* LEFT PANEL: Raw Transcript */}
                            <div className="w-[38%] bg-surface-light border-r border-surface-hover flex flex-col relative group/left">
                                <div className="p-4 border-b border-surface-hover bg-surface-light/90 backdrop-blur sticky top-0 z-10">
                                    <div className="text-[10px] font-bold text-ink-secondary uppercase tracking-widest flex items-center gap-2">
                                        <Mic size={12} /> {t.landing.mockup.sourceTitle}
                                    </div>
                                </div>

                                <div className="p-6 space-y-6 overflow-hidden relative">
                                    {/* Gradient Mask */}
                                    <div className="absolute top-0 left-0 right-0 h-4 bg-gradient-to-b from-surface-light to-transparent z-10"></div>

                                    {/* Item 1 (Ghost) - Increased Visibility */}
                                    <div className="flex gap-3 opacity-60 blur-[0.3px] transition-all duration-300 hover:opacity-80 hover:blur-none">
                                        <div className="w-8 h-8 rounded-full bg-white border border-surface-border flex items-center justify-center text-[10px] font-bold text-ink-secondary shrink-0">S</div>
                                        <div className="space-y-2 w-full">
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs font-bold text-ink-secondary">{t.landing.mockup.sarahName}</span>
                                                <span className="text-xs font-bold text-ink-dark">10:04</span>
                                            </div>
                                            <p className="text-xs text-ink-secondary leading-relaxed text-left">
                                                {t.landing.mockup.sarahText}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Item 2 (Active Source) */}
                                    <div className="relative p-4 -mx-4 rounded-xl bg-white border border-surface-divider shadow-[0_2px_12px_rgba(0,0,0,0.04)] ring-1 ring-brand-500/10 transition-all duration-300 group-hover:bg-surface-border/30 group-hover:border-brand-500/30">
                                        {/* Anchor Point */}
                                        <div ref={sourceRef} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-3 h-3 bg-brand-500 border-2 border-white rounded-full z-20 shadow-sm hidden md:block"></div>

                                        <div className="flex gap-3">
                                            <div className="w-8 h-8 rounded-full bg-surface-border border border-surface-divider flex items-center justify-center text-[10px] font-bold text-brand-800 shrink-0">D</div>
                                            <div className="space-y-1 w-full">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs font-bold text-brand-900">{t.landing.mockup.davidName}</span>
                                                    {/* UPDATED TIMESTAMP: Bigger, more obvious */}
                                                    <span className="text-xs font-bold text-brand-800">10:12</span>
                                                </div>
                                                <p className="text-xs text-brand-800 leading-relaxed text-left">
                                                    {t.landing.mockup.davidText}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Item 3 (Ghost) - Increased Visibility */}
                                    <div className="flex gap-3 opacity-60 blur-[0.3px] transition-all duration-300 hover:opacity-80 hover:blur-none">
                                        <div className="w-8 h-8 rounded-full bg-white border border-surface-border flex items-center justify-center text-[10px] font-bold text-ink-secondary shrink-0">M</div>
                                        <div className="space-y-2 w-full">
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs font-bold text-ink-secondary">{t.landing.mockup.mikeName}</span>
                                                <span className="text-xs font-bold text-ink-dark">10:15</span>
                                            </div>
                                            <p className="text-xs text-ink-secondary leading-relaxed text-left">
                                                {t.landing.mockup.mikeText}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* RIGHT PANEL: Structured Report - PREMIUM / LUXURY UPGRADE */}
                            <div className="flex-1 bg-prestige-50 flex flex-col relative overflow-hidden shadow-[inset_6px_0_20px_-10px_rgba(0,0,0,0.03)] border-l border-prestige-300/20">
                                {/* Premium Header Bar */}
                                <div className="p-4 border-b border-surface-hover flex items-center justify-between sticky top-0 bg-prestige-50/95 backdrop-blur z-10">
                                    <div className="text-[10px] font-bold text-ink-tertiary uppercase tracking-widest flex items-center gap-2">
                                        <FileText size={12} className="text-accent-amber" /> {t.landing.mockup.reportTitle}
                                    </div>
                                    <div className="flex gap-2">
                                        <div className="h-1.5 w-12 bg-surface-hover rounded-full"></div>
                                        <div className="h-1.5 w-8 bg-surface-hover rounded-full"></div>
                                    </div>
                                </div>

                                <div className="p-8 md:p-12 overflow-y-auto custom-scrollbar">
                                    {/* Document Header - More Premium Serif */}
                                    <div className="mb-8 border-b border-prestige-300/30 pb-6 relative">
                                        <div className="absolute top-0 right-0 text-[60px] leading-none text-prestige-300/10 font-serif font-bold pointer-events-none">A+</div>
                                        <h1 className="text-3xl font-serif font-bold text-prestige-900 mb-3 tracking-tight">{t.landing.mockup.docTitle}</h1>
                                        <div className="flex gap-4 text-[10px] text-ink-tertiary uppercase tracking-wide font-medium">
                                            <span>{language === 'en' ? 'Oct 24, 2024' : '2024年 10月 24日'}</span>
                                            <span className="text-prestige-300">•</span>
                                            <span>{t.landing.mockup.preparedBy}</span>
                                        </div>
                                    </div>

                                    {/* Section 1 */}
                                    <div className="mb-8 opacity-60 hover:opacity-100 transition-opacity duration-500">
                                        <h2 className="text-sm font-bold text-prestige-900 mb-3 flex items-center gap-2 font-serif">
                                            <span className="text-ink-muted font-sans">1.</span> {t.landing.mockup.summaryTitle}
                                        </h2>
                                        <p className="text-xs text-ink-secondary leading-relaxed pl-5 border-l border-prestige-300/30 text-left font-serif">
                                            {t.landing.mockup.summaryText}
                                        </p>
                                    </div>

                                    {/* Section 2 (Active Linked) - Premium Styling */}
                                    <div className="relative group/doc">
                                        {/* Anchor Point */}
                                        <div ref={targetRef} className="absolute left-0 top-[60px] -translate-x-1/2 w-3 h-3 bg-brand-500 border-2 border-white rounded-full z-20 shadow-sm hidden md:block"></div>

                                        <div className="absolute -left-6 -right-6 top-0 bottom-0 bg-prestige-300/5 opacity-0 group-hover/doc:opacity-100 transition-opacity duration-300 rounded-lg pointer-events-none border border-prestige-300/20"></div>

                                        <h2 className="text-sm font-bold text-prestige-900 mb-4 flex items-center gap-2 relative z-10 font-serif">
                                            <span className="text-accent-amber font-sans">2.</span> {t.landing.mockup.riskTitle}
                                        </h2>

                                        <div className="pl-5 border-l-2 border-prestige-300 space-y-5 relative z-10">
                                            {/* Sub-section 2.1 */}
                                            <div>
                                                <h3 className="text-xs font-bold text-prestige-800 mb-2 tracking-wide">{language === 'en' ? '2.1' : '2.1'} {t.landing.mockup.scalabilityTitle}</h3>
                                                <div className="space-y-3">
                                                    <div className="flex items-start gap-2">
                                                        <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-accent-amber shrink-0"></div>
                                                        <p className="text-xs text-ink-dark leading-relaxed text-left">
                                                            {t.landing.mockup.sqlLimit}
                                                        </p>
                                                    </div>
                                                    <div className="flex items-start gap-2">
                                                        <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-feedback-error shrink-0"></div>
                                                        <p className="text-xs text-ink-dark leading-relaxed text-left">
                                                            {t.landing.mockup.latencyRisk}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Sub-section 2.2 - Premium Card Look */}
                                            <div className="bg-white rounded-lg p-4 border border-prestige-300/40 shadow-[0_4px_12px_rgba(238,199,142,0.15)] relative overflow-hidden">
                                                <div className="absolute top-0 right-0 w-8 h-8 bg-prestige-300/10 rounded-bl-full"></div>
                                                <h3 className="text-xs font-bold text-prestige-800 mb-2 flex items-center gap-2">
                                                    {language === 'en' ? '2.2' : '2.2'} {t.landing.mockup.actionTitle}
                                                    <span className="px-1.5 py-0.5 rounded text-[9px] bg-prestige-300/20 text-accent-amber-light font-bold uppercase border border-prestige-300/30">{t.landing.mockup.highPriority}</span>
                                                </h3>
                                                <p className="text-xs text-ink-dark leading-relaxed text-left">
                                                    {t.landing.mockup.redisAction}
                                                </p>
                                                <div className="mt-3 flex items-center gap-1 text-[9px] text-accent-amber font-medium cursor-pointer hover:underline opacity-80 hover:opacity-100 transition-opacity">
                                                    <LinkIcon size={10} />
                                                    <span>{t.landing.mockup.sourceTag}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* SVG CONNECTION LINE - FLOW EFFECT */}
                            <svg className="absolute inset-0 pointer-events-none z-50 overflow-visible hidden md:block">
                                <defs>
                                    <linearGradient id="trace-line" x1="0%" y1="0%" x2="100%" y2="0%">
                                        <stop offset="0%" stopColor="#84cc16" stopOpacity="0.1" />
                                        <stop offset="40%" stopColor="#84cc16" stopOpacity="0.8" />
                                        <stop offset="60%" stopColor="#84cc16" stopOpacity="0.8" />
                                        <stop offset="100%" stopColor="#84cc16" stopOpacity="0.1" />
                                    </linearGradient>
                                    <filter id="glow">
                                        <feGaussianBlur stdDeviation="2" result="coloredBlur" />
                                        <feMerge>
                                            <feMergeNode in="coloredBlur" />
                                            <feMergeNode in="SourceGraphic" />
                                        </feMerge>
                                    </filter>
                                </defs>

                                {/* Static faint path */}
                                <path
                                    d={connectionPath}
                                    fill="none"
                                    stroke="url(#trace-line)"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    filter="url(#glow)"
                                    className="opacity-50"
                                    vectorEffect="non-scaling-stroke"
                                />

                                {/* Flowing dashed line */}
                                <path
                                    d={connectionPath}
                                    fill="none"
                                    stroke="#84cc16"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeDasharray="10 10"
                                    className="animate-flow opacity-80"
                                    vectorEffect="non-scaling-stroke"
                                />

                                {/* Traveling Particle */}
                                <circle r="4" fill="#FFFFFF" stroke="#84cc16" strokeWidth="2" filter="url(#glow)">
                                    <animateMotion
                                        dur="2s"
                                        repeatCount="indefinite"
                                        path={connectionPath}
                                    />
                                </circle>
                            </svg>

                        </div>
                    </div>
                </div>
            </section>

            {/* Feature Bento Grid */}
            <section className="w-full max-w-7xl mx-auto px-6 pb-32">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 auto-rows-[420px]">

                    {/* Feature 1: Expert Synthesis (Sage Green Theme) */}
                    <div className="md:col-span-2 bg-surface-border rounded-modal p-10 overflow-hidden relative group border border-surface-divider flex flex-col justify-center transition-all hover:shadow-lg">
                        <div className="relative z-30 max-w-xs pointer-events-none">
                            <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center mb-6 shadow-sm border border-surface-divider text-brand-800">
                                <FileText size={28} />
                            </div>
                            <h3 className="text-3xl font-serif font-bold mb-4 text-brand-900">{t.landing.feature1Title}</h3>
                            <p className="text-brand-700 text-lg leading-relaxed">
                                {t.landing.feature1Desc}
                            </p>
                        </div>

                        {/* Visual: Synthesis Process */}
                        <div className="absolute right-0 top-0 w-[60%] h-full hidden md:flex items-center justify-center z-10 perspective-[1000px]">

                            <div className="absolute w-80 h-80 bg-white/40 rounded-full blur-3xl translate-x-10 translate-y-10 group-hover:bg-white/60 transition-colors duration-700"></div>

                            <div className="relative w-96 h-96 flex items-center justify-center">

                                {/* Card 1: The CHAOS (Aligned Narrative) */}
                                <div className="absolute w-64 h-80 bg-surface-light rounded-xl border border-surface-divider p-5 shadow-sm transform transition-all duration-700 ease-out origin-center
                            rotate-[-12deg] translate-x-[-70px] translate-y-[20px] scale-95 z-10
                            group-hover:translate-x-[-150px] group-hover:rotate-[-15deg] group-hover:scale-100 group-hover:bg-white"
                                >
                                    <div className="flex items-center gap-2 mb-4 border-b border-surface-hover pb-2">
                                        <div className="w-2 h-2 rounded-full bg-red-400 animate-pulse"></div>
                                        <span className="text-[10px] font-mono text-ink-muted font-bold uppercase tracking-wider">{t.landing.bento.rawAudioLog}</span>
                                    </div>

                                    <div className="space-y-4 font-mono text-[10px] text-ink-muted leading-relaxed select-none">
                                        <p className="opacity-80 italic">{t.landing.bento.rawLogLine1}</p>
                                        <p className="text-ink-secondary font-bold">{t.landing.bento.rawLogLine2}</p>
                                        <p className="opacity-80 italic">{t.landing.bento.rawLogLine3}</p>
                                    </div>
                                </div>

                                {/* Central Transformation Effect */}
                                <div className="absolute z-15 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-100 pointer-events-none">
                                    <div className="flex flex-col items-center gap-1">
                                        <div className="w-12 h-1 bg-brand-500/50 rounded-full blur-sm"></div>
                                        <ArrowRight size={32} className="text-brand-500 animate-pulse" strokeWidth={3} />
                                        <div className="w-12 h-1 bg-brand-500/50 rounded-full blur-sm"></div>
                                    </div>
                                </div>

                                {/* Card 2: The ORDER */}
                                <div className="absolute w-[300px] h-auto min-h-[340px] bg-white rounded-xl border border-surface-divider/80 p-6 shadow-2xl transform transition-all duration-700 ease-out origin-center
                            rotate-[4deg] translate-x-[50px] translate-y-[-10px] z-20
                            group-hover:translate-x-[90px] group-hover:rotate-[2deg] group-hover:scale-105"
                                >
                                    <div className="flex items-center justify-between mb-6 border-b border-surface-hover pb-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-lg bg-surface-border flex items-center justify-center text-brand-800 shadow-sm border border-surface-divider">
                                                <Zap size={16} fill="currentColor" />
                                            </div>
                                            <span className="text-xs font-bold text-brand-900 uppercase tracking-wide">{t.landing.bento.strategicPlan}</span>
                                        </div>
                                        <MoreHorizontal size={14} className="text-ink-muted" />
                                    </div>

                                    <div className="space-y-6">
                                        <div>
                                            <div className="text-[10px] font-bold text-ink-muted uppercase tracking-wider mb-2">{t.landing.bento.shortTermPriority}</div>
                                            <div className="flex items-start gap-3">
                                                <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand-500 shrink-0 shadow-sm"></div>
                                                <div className="space-y-2">
                                                    <p className="text-xs text-brand-900 font-semibold leading-relaxed">
                                                        {t.landing.bento.reactivationTitle}
                                                    </p>
                                                    <p className="text-[10px] text-ink-secondary leading-relaxed border-l-2 border-surface-hover pl-2">
                                                        {t.landing.bento.reactivationDesc}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        <div>
                                            <div className="flex items-start gap-3">
                                                <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0 shadow-sm"></div>
                                                <div className="space-y-2">
                                                    <p className="text-xs text-brand-900 font-semibold leading-relaxed">
                                                        {t.landing.bento.aiApps}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="space-y-2 opacity-10 pt-2">
                                            <div className="h-1.5 bg-brand-900 rounded-full w-full"></div>
                                            <div className="h-1.5 bg-brand-900 rounded-full w-5/6"></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Feature 3: Templates (Soft White Theme) */}
                    <div className="bg-surface-light rounded-modal p-10 flex flex-col justify-between overflow-hidden group hover:shadow-xl transition-all relative border border-surface-hover">
                        <div className="relative z-10">
                            <div className="w-14 h-14 bg-surface rounded-2xl flex items-center justify-center mb-6 border border-surface-hover text-ink-secondary shadow-sm">
                                <Settings size={28} />
                            </div>
                            <h3 className="text-2xl font-serif font-bold mb-3 text-brand-900">{t.landing.feature3Title}</h3>
                            <p className="text-ink-secondary text-sm leading-relaxed">
                                {t.landing.feature3Desc}
                            </p>
                        </div>

                        {/* Visual: Orbiting Export Options */}
                        <div className="mt-8 relative h-48 flex items-center justify-center">
                            <div className="absolute w-40 h-40 bg-surface-border/50 rounded-full blur-2xl animate-pulse"></div>

                            {/* Central Document */}
                            <div className="relative z-10 w-24 h-32 bg-white rounded-xl border border-surface-hover shadow-xl flex flex-col items-center p-3 gap-2 group-hover:scale-105 transition-transform duration-300">
                                <div className="w-full h-1.5 bg-surface rounded-full mb-1"></div>
                                <div className="w-2/3 h-1.5 bg-surface rounded-full mb-3 self-start"></div>
                                <div className="space-y-1.5 w-full">
                                    <div className="w-full h-1 bg-surface rounded-full"></div>
                                    <div className="w-full h-1 bg-surface rounded-full"></div>
                                    <div className="w-full h-1 bg-surface rounded-full"></div>
                                </div>
                                <div className="mt-auto w-8 h-8 rounded-lg bg-surface-border flex items-center justify-center text-brand-800">
                                    <Zap size={16} fill="currentColor" />
                                </div>
                            </div>

                            {/* Orbiting Satellites */}
                            <div className="absolute bottom-2 -right-2 bg-[#2b579a] w-12 h-12 rounded-xl flex items-center justify-center shadow-lg z-20 ring-4 ring-surface-light transform group-hover:translate-x-2 group-hover:translate-y-2 transition-transform duration-300">
                                <div className="text-white font-bold text-xl font-serif">W</div>
                            </div>
                            <div className="absolute bottom-4 left-2 bg-feedback-error w-10 h-10 rounded-lg flex items-center justify-center text-white shadow-lg z-20 ring-4 ring-surface-light transform group-hover:-translate-x-2 group-hover:translate-y-1 transition-transform duration-300 delay-75">
                                <span className="text-[9px] font-bold">PDF</span>
                            </div>
                            <div className="absolute top-6 -right-1 bg-ink-dark w-9 h-9 rounded-lg flex items-center justify-center text-white shadow-lg z-0 ring-4 ring-surface-light transform group-hover:translate-x-2 group-hover:-translate-y-2 transition-transform duration-300 delay-100">
                                <FileCode size={16} />
                            </div>
                            <div className="absolute top-8 left-4 bg-ink-muted w-8 h-8 rounded-lg flex items-center justify-center text-white shadow-lg z-0 ring-4 ring-surface-light transform group-hover:-translate-x-2 group-hover:-translate-y-1 transition-transform duration-300 delay-150">
                                <FileText size={14} />
                            </div>
                        </div>
                    </div>

                    {/* Feature 4: Security (Soft White Theme) */}
                    <div className="bg-surface-light rounded-modal p-10 flex flex-col justify-between border border-surface-hover relative overflow-hidden group hover:shadow-xl transition-all">
                        <div className="relative z-10">
                            <div className="w-14 h-14 bg-surface rounded-2xl flex items-center justify-center mb-6 border border-surface-hover text-ink-secondary">
                                <ShieldCheck size={28} />
                            </div>
                            <h3 className="text-2xl font-serif font-bold mb-3 text-brand-900">{t.landing.feature4Title}</h3>
                            <p className="text-ink-secondary text-sm leading-relaxed">
                                {t.landing.feature4Desc}
                            </p>
                        </div>

                        {/* Visual: Digital Vault */}
                        <div className="mt-auto relative h-40 flex items-center justify-center perspective-[1000px]">
                            <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.03)_1px,transparent_1px)] bg-[size:16px_16px] [mask-image:radial-gradient(ellipse_at_center,black,transparent_70%)]"></div>

                            <svg className="absolute inset-0 w-full h-full pointer-events-none z-0">
                                <line x1="20%" y1="50%" x2="50%" y2="50%" stroke="#e5e7eb" strokeWidth="2" strokeDasharray="4 2" />
                                <line x1="80%" y1="50%" x2="50%" y2="50%" stroke="#e5e7eb" strokeWidth="2" strokeDasharray="4 2" />
                            </svg>

                            <div className="absolute left-0 top-1/2 -translate-y-1/2 bg-white border border-surface-hover px-3 py-1.5 rounded-lg shadow-sm flex items-center gap-2 z-20 group-hover:scale-105 transition-transform duration-300">
                                <div className="w-2 h-2 rounded-full bg-green-500 shadow-[0_0_8px_#22c55e]"></div>
                                <span className="text-[10px] font-bold text-ink-secondary tracking-wide">SOC2 TYPE II</span>
                            </div>

                            <div className="relative z-10 w-20 h-20 bg-ink-dark rounded-2xl shadow-2xl flex items-center justify-center transform transition-transform duration-500 group-hover:scale-105 ring-4 ring-surface">
                                <div className="absolute inset-0 rounded-2xl overflow-hidden">
                                    <div className="absolute inset-x-0 h-full bg-gradient-to-b from-transparent via-prestige-300/20 to-transparent animate-[scan_3s_ease-in-out_infinite] translate-y-[-100%]"></div>
                                    <div className="absolute inset-x-0 h-0.5 bg-prestige-300/50 blur-[2px] animate-[scan_3s_ease-in-out_infinite] translate-y-[-100%]"></div>
                                </div>

                                <Lock size={32} className="text-white relative z-10" strokeWidth={2} />

                                <div className="absolute top-3 right-3 w-1.5 h-1.5 bg-prestige-300 rounded-full animate-pulse"></div>
                            </div>

                            <div className="absolute right-0 top-1/2 -translate-y-1/2 bg-white border border-surface-hover px-3 py-1.5 rounded-lg shadow-sm flex items-center gap-2 z-20 group-hover:scale-105 transition-transform duration-300">
                                <Fingerprint size={14} className="text-ink-muted" />
                                <span className="text-[10px] font-bold text-ink-secondary tracking-wide">AES-256</span>
                            </div>
                        </div>
                    </div>

                    {/* Feature 2: Zero Hallucinations (Brown Theme - "Team Plan" style) */}
                    <div className="md:col-span-2 bg-ink-dark text-white rounded-modal p-10 relative overflow-hidden flex items-center justify-between group transition-all hover:scale-[1.01] hover:shadow-2xl border border-ink-secondary">

                        <div className="relative z-20 w-[30%] flex flex-col justify-center h-full pr-4">
                            <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center mb-6 backdrop-blur-sm border border-white/10 text-prestige-300">
                                <LinkIcon size={28} />
                            </div>
                            <h3 className="text-3xl font-serif font-bold mb-4">{t.landing.feature2Title}</h3>
                            <p className="text-prestige-300/80 text-lg leading-relaxed">
                                {t.landing.feature2Desc}
                            </p>
                        </div>

                        <div className="absolute right-0 top-0 w-[70%] h-full flex items-center justify-center bg-gradient-to-l from-prestige-900 to-transparent z-10 overflow-hidden">

                            <div className="relative w-[500px] h-[340px] perspective-[1000px]">

                                {/* Source Card */}
                                <div className="absolute left-[20px] top-[40px] w-[280px] bg-surface-light/90 backdrop-blur-sm border border-surface-divider/50 p-5 rounded-2xl shadow-xl z-20 transition-all duration-500 ease-out 
                             opacity-60 group-hover:opacity-100 group-hover:translate-x-0 group-hover:rotate-[-2deg] scale-95 group-hover:scale-100 origin-center text-brand-900">

                                    <div className="flex items-center justify-between mb-4 pb-2 border-b border-surface-hover">
                                        <span className="text-[10px] font-bold text-ink-muted uppercase tracking-wider">{t.landing.bento.transcriptHeader} • 04:12</span>
                                        <div className="w-1.5 h-1.5 rounded-full bg-brand-500"></div>
                                    </div>

                                    <div className="space-y-4">
                                        <div className="flex gap-3 p-2 -mx-2">
                                            <div className="w-6 h-6 rounded-full bg-surface-border flex items-center justify-center text-[10px] font-bold text-ink-secondary mt-0.5 shrink-0">A</div>
                                            <div>
                                                <div className="text-[10px] font-bold text-ink-secondary mb-0.5">{t.landing.bento.alexName}</div>
                                                <p className="text-[10px] text-ink-muted leading-relaxed">
                                                    {t.landing.bento.alexText}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="flex gap-3 p-2 -mx-2 rounded-lg transition-colors duration-300 group-hover:bg-surface-border border border-transparent group-hover:border-surface-divider">
                                            <div className="w-6 h-6 rounded-full bg-prestige-100 flex items-center justify-center text-[10px] font-bold text-ink-tertiary mt-0.5 shrink-0">S</div>
                                            <div>
                                                <div className="text-[10px] font-bold text-brand-800 mb-0.5">{t.landing.bento.sarahRole}</div>
                                                <p className="text-[10px] text-brand-700 leading-relaxed">
                                                    {t.landing.bento.sarahText}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Insight Card */}
                                <div className="absolute right-[20px] top-[100px] w-[270px] bg-white text-brand-900 p-6 rounded-2xl shadow-2xl border border-surface-divider transition-transform duration-500 group-hover:translate-x-4 group-hover:translate-y-2 z-30">
                                    <div className="flex items-center justify-between mb-5">
                                        <div className="flex items-center gap-2">
                                            <div className="w-2 h-2 rounded-full bg-brand-500"></div>
                                            <span className="text-xs font-bold text-brand-900 uppercase tracking-wide">{t.landing.bento.marketAnalysis}</span>
                                        </div>
                                        <div className="text-brand-800 bg-surface-border p-1.5 rounded-md hover:bg-surface-divider transition-colors">
                                            <LinkIcon size={14} />
                                        </div>
                                    </div>

                                    <div className="space-y-4">
                                        <div>
                                            <div className="text-[10px] font-semibold text-ink-muted mb-1">{t.landing.bento.marketScale}</div>
                                            <p className="text-sm font-medium text-brand-900 pl-3 border-l-2 border-surface-hover">
                                                {t.landing.bento.scaleValue}
                                            </p>
                                        </div>

                                        <div className="p-2 -mx-2 rounded-lg transition-colors duration-300 group-hover:bg-surface-border border border-transparent group-hover:border-surface-divider">
                                            <div className="text-[10px] font-semibold text-ink-muted mb-1">{t.landing.bento.growthDriver}</div>
                                            <div className="pl-3 border-l-2 border-transparent group-hover:border-brand-500 transition-colors">
                                                <p className="text-sm font-medium text-brand-900 leading-relaxed">
                                                    {t.landing.bento.driverText}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Connecting Fiber */}
                                <svg className="absolute inset-0 w-full h-full pointer-events-none z-50 overflow-visible opacity-0 group-hover:opacity-100 transition-opacity duration-700" viewBox="0 0 500 340">
                                    <defs>
                                        <filter id="glow-bold">
                                            <feGaussianBlur stdDeviation="1.5" result="coloredBlur" />
                                            <feMerge>
                                                <feMergeNode in="coloredBlur" />
                                                <feMergeNode in="SourceGraphic" />
                                            </feMerge>
                                        </filter>
                                    </defs>

                                    <path
                                        d="M 165 235 Q 165 275 212 275"
                                        fill="none"
                                        stroke="#4d7c0f"
                                        strokeWidth="3"
                                        strokeOpacity="0.3"
                                        strokeLinecap="round"
                                    />

                                    <path
                                        d="M 165 235 Q 165 275 212 275"
                                        fill="none"
                                        stroke="#84cc16"
                                        strokeWidth="3"
                                        strokeLinecap="round"
                                        filter="url(#glow-bold)"
                                        strokeDasharray="100 200"
                                        strokeDashoffset="100"
                                    >
                                        <animate
                                            attributeName="stroke-dashoffset"
                                            from="300"
                                            to="0"
                                            dur="1.2s"
                                            repeatCount="indefinite"
                                            calcMode="spline"
                                            keyTimes="0;1"
                                            keySplines="0.4 0 0.2 1"
                                        />
                                    </path>

                                    <circle cx="165" cy="235" r="4" fill="#84cc16" stroke="#1a2e05" strokeWidth="1" />
                                    <circle cx="212" cy="275" r="4" fill="#84cc16" stroke="#1a2e05" strokeWidth="1" />
                                </svg>

                                {/* Verification Badge */}
                                <div className="absolute bottom-2 left-4 z-40">
                                    <div className="bg-prestige-900/80 backdrop-blur-md border border-white/10 rounded-xl px-4 py-3 flex items-center gap-3 shadow-2xl transform translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500 delay-200">
                                        <div className="w-10 h-10 rounded-lg bg-brand-500/10 flex items-center justify-center border border-brand-500/20">
                                            <Eye size={20} className="text-brand-500" />
                                        </div>
                                        <div>
                                            <div className="text-[10px] text-white/60 font-bold uppercase tracking-wider mb-0.5">{t.landing.bento.verification}</div>
                                            <div className="text-sm font-bold text-white flex items-center gap-1.5">
                                                {t.landing.bento.sourceMatched} <CheckCircle2 size={14} className="text-brand-500 fill-brand-900" />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>

                </div>
            </section>

            {/* Testimonials (Updated to Soft White Theme) */}
            <section className="w-full py-20 bg-surface">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-serif font-bold mb-4 text-brand-900">{t.landing.testimonialsTitle}</h2>
                        <p className="text-brand-700">{t.landing.testimonialsSub}</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {t.landing.testimonials.map((testimonial, i) => (
                            <div key={i} className="bg-surface-light p-8 rounded-2xl border border-surface-hover hover:shadow-lg transition-shadow h-full flex flex-col justify-between">
                                <div>
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="w-10 h-10 rounded-full bg-surface-border border border-surface-divider flex items-center justify-center font-bold text-brand-800 text-sm">
                                            {testimonial.name.charAt(0)}
                                        </div>
                                        <div>
                                            <div className="font-semibold text-brand-900">{testimonial.name}</div>
                                            <div className="text-xs text-ink-secondary">{testimonial.role}</div>
                                        </div>
                                    </div>
                                    <p className="text-ink-secondary leading-relaxed text-sm">"{testimonial.text}"</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Partners Wall - Updated Colors */}
            <section className="w-full pb-32 pt-10">
                <div className="max-w-7xl mx-auto px-6 text-center">
                    <p className="text-sm font-semibold text-ink-muted uppercase tracking-widest mb-10">{t.landing.partnersTitle}</p>
                    <div className="flex flex-wrap justify-center gap-12 opacity-30 grayscale hover:opacity-100 hover:grayscale-0 transition-all duration-500">
                        {/* Placeholder Logos with darker tint */}
                        <div className="h-8 w-32 bg-ink-dark rounded mask-logo"></div>
                        <div className="h-8 w-24 bg-ink-dark rounded mask-logo"></div>
                        <div className="h-8 w-40 bg-ink-dark rounded mask-logo"></div>
                        <div className="h-8 w-28 bg-ink-dark rounded mask-logo"></div>
                        <div className="h-8 w-32 bg-ink-dark rounded mask-logo"></div>
                    </div>
                </div>
            </section>

            {/* ROI Calculator - Brown Theme ("Team Plan") */}
            <section className="w-full bg-ink-dark text-white py-32 rounded-t-[48px] relative overflow-hidden border-t border-ink-secondary">
                {/* Subtle noise/gradient background */}
                <div className="absolute top-0 left-0 w-full h-full opacity-30 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-prestige-900 via-transparent to-black pointer-events-none"></div>

                <div className="max-w-7xl mx-auto px-6 relative z-10">
                    <div className="text-center mb-20">
                        <h2 className="text-3xl md:text-5xl font-bold mb-6 tracking-tight text-white">{t.landing.calcTitle}</h2>
                        <p className="text-prestige-300/80 max-w-2xl mx-auto text-lg">{t.landing.calcDesc}</p>
                    </div>

                    <div className="flex flex-col lg:flex-row gap-16">

                        {/* Left Side: Inputs */}
                        <div className="w-full lg:w-[35%] space-y-12 py-4">
                            <div className="space-y-6">
                                <div className="flex justify-between items-end">
                                    <label className="text-sm font-medium text-prestige-300 uppercase tracking-wider">{t.landing.teamSize}</label>
                                    <span className="font-bold text-4xl italic font-serif text-white">{teamSize} <span className="text-xl text-white/50 font-sans not-italic font-normal">{language === 'en' ? 'ppl' : '人'}</span></span>
                                </div>
                                <input
                                    type="range"
                                    min="1" max="50"
                                    value={teamSize}
                                    onChange={(e) => setTeamSize(parseInt(e.target.value))}
                                    style={getSliderStyle(teamSize, 1, 50, 'green')}
                                    className="w-full h-4 bg-prestige-900 rounded-full appearance-none cursor-pointer focus:outline-none dark-slider"
                                />
                            </div>
                            <div className="space-y-6">
                                <div className="flex justify-between items-end">
                                    <label className="text-sm font-medium text-prestige-300 uppercase tracking-wider">{t.landing.meetingsWeek}</label>
                                    <span className="font-bold text-4xl italic font-serif text-white">{meetingsPerWeek} <span className="text-xl text-white/50 font-sans not-italic font-normal">{language === 'en' ? 'avg' : '人均'}</span></span>
                                </div>
                                <input
                                    type="range"
                                    min="1" max="25"
                                    value={meetingsPerWeek}
                                    onChange={(e) => setMeetingsPerWeek(parseInt(e.target.value))}
                                    style={getSliderStyle(meetingsPerWeek, 1, 25, 'gold')}
                                    className="w-full h-4 bg-prestige-900 rounded-full appearance-none cursor-pointer focus:outline-none dark-slider"
                                />
                            </div>
                            <div className="space-y-6">
                                <div className="flex justify-between items-end">
                                    <label className="text-sm font-medium text-prestige-300 uppercase tracking-wider">{t.landing.hourlyRate}</label>
                                    <span className="font-bold text-4xl italic font-serif text-white">${hourlyRate}</span>
                                </div>
                                <input
                                    type="range"
                                    min="20" max="300" step="5"
                                    value={hourlyRate}
                                    onChange={(e) => setHourlyRate(parseInt(e.target.value))}
                                    style={getSliderStyle(hourlyRate, 20, 300, 'rust')}
                                    className="w-full h-4 bg-prestige-900 rounded-full appearance-none cursor-pointer focus:outline-none dark-slider"
                                />
                            </div>
                        </div>

                        {/* Divider */}
                        <div className="hidden lg:block w-px bg-gradient-to-b from-transparent via-ink-secondary to-transparent"></div>

                        {/* Right Side: Results */}
                        <div className="flex-1 flex flex-col gap-6">

                            {/* Top Card: Annual Savings */}
                            <div className="bg-prestige-900 rounded-modal relative group shadow-2xl flex items-center justify-between border border-white/10 transition-all duration-500 hover:border-prestige-300/30 overflow-hidden z-10 min-h-[220px]">
                                {/* Text Container */}
                                <div className="relative z-20 p-10 flex flex-col justify-center h-full max-w-[60%]">
                                    <div className="text-xs text-prestige-300 font-bold uppercase tracking-widest mb-3 flex items-center gap-2">
                                        <span className="w-1.5 h-1.5 rounded-full bg-prestige-300 animate-pulse"></span>
                                        {t.landing.annualSavings}
                                    </div>
                                    <div className="text-7xl md:text-8xl font-bold font-serif text-white mb-2 tracking-tighter transition-all duration-300 transform origin-left">${moneySaved.toLocaleString()}</div>
                                    <div className="text-white/60 text-xs mt-4 max-w-[200px] leading-relaxed opacity-60">
                                        {language === 'en' ? 'Calculated based on average note-taking & review time saved.' : '基于平均节省的笔记和复盘时间计算。'}
                                    </div>
                                </div>

                                {/* Visual */}
                                <div className="absolute right-0 top-0 h-full w-[40%] flex items-center justify-center">
                                    <svg className="absolute inset-0 w-full h-full text-prestige-300/10" viewBox="0 0 100 100" preserveAspectRatio="none">
                                        <path d="M0 100 C 30 80, 50 50, 100 20 L 100 100 Z" fill="currentColor" />
                                        <path d="M0 100 C 30 80, 50 50, 100 20" stroke="currentColor" strokeWidth="1" fill="none" className="text-prestige-300/30" />
                                    </svg>

                                    <div className="relative w-24 h-24 bg-ink-dark rounded-2xl border border-white/10 flex items-center justify-center shadow-2xl transform rotate-6 group-hover:rotate-0 group-hover:scale-110 transition-all duration-500">
                                        <div className="absolute inset-0 bg-prestige-300/10 rounded-2xl"></div>
                                        <Zap size={40} className="text-prestige-300 fill-prestige-300" />
                                        <div className="absolute -top-6 -right-6 text-white animate-pulse delay-100"><Sparkles size={20} /></div>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-6 h-48">
                                {/* Bottom Left: Hours Reclaimed */}
                                <div className="bg-prestige-900 p-8 rounded-modal border border-white/5 relative overflow-hidden flex flex-col justify-between group hover:border-prestige-300/20 transition-colors">
                                    <div className="relative z-10">
                                        <div className="text-5xl font-bold font-serif text-white mb-2 tracking-tight">{hoursSavedPerYear.toLocaleString()}</div>
                                        <div className="text-prestige-300/70 text-xs uppercase font-bold tracking-wider">{t.landing.hoursReclaimed}</div>
                                    </div>

                                    <div className="absolute -bottom-6 -right-6 w-40 h-40 opacity-80 group-hover:opacity-100 transition-opacity">
                                        <div className="relative w-full h-full flex items-center justify-center">
                                            <div className="absolute inset-0 bg-gradient-to-br from-prestige-950/50 to-transparent rounded-full blur-xl"></div>
                                            <svg className="absolute inset-0 w-full h-full text-ink-secondary" viewBox="0 0 100 100">
                                                {Array.from({ length: 12 }).map((_, i) => (
                                                    <line
                                                        key={i}
                                                        x1="50" y1="5" x2="50" y2="10"
                                                        stroke="currentColor"
                                                        strokeWidth="2"
                                                        transform={`rotate(${i * 30} 50 50)`}
                                                    />
                                                ))}
                                            </svg>
                                            <svg className="absolute inset-0 w-full h-full animate-[spin_4s_linear_infinite_reverse]" viewBox="0 0 100 100">
                                                <line x1="50" y1="50" x2="50" y2="15" stroke="#eec78e" strokeWidth="3" strokeLinecap="round" />
                                                <circle cx="50" cy="50" r="4" fill="white" />
                                            </svg>
                                            <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 100 100">
                                                <circle cx="50" cy="50" r="44" fill="none" stroke="rgba(238, 199, 142, 0.1)" strokeWidth="4" />
                                                <circle
                                                    cx="50" cy="50" r="44"
                                                    fill="none"
                                                    stroke="#eec78e"
                                                    strokeWidth="4"
                                                    strokeDasharray="276"
                                                    strokeDashoffset={276 - (276 * (Math.min(hoursSavedPerYear / 5000, 1)))}
                                                    strokeLinecap="round"
                                                    className="transition-all duration-1000 ease-out"
                                                />
                                            </svg>
                                        </div>
                                    </div>
                                </div>

                                {/* Bottom Right: Info Fidelity */}
                                <div className="bg-prestige-900 p-8 rounded-modal border border-white/5 relative overflow-hidden flex flex-col justify-between hover:border-prestige-300/20 transition-colors">
                                    <div className="relative z-10">
                                        <div className="text-5xl font-bold font-serif text-white mb-2 tracking-tight">100<span className="text-2xl text-prestige-300">%</span></div>
                                        <div className="text-prestige-300/70 text-xs uppercase font-bold tracking-wider">{t.landing.infoFidelity}</div>
                                    </div>

                                    <div className="absolute bottom-6 right-6 w-24 h-24">
                                        <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                                            <circle cx="50" cy="50" r="40" fill="none" stroke="#2a2320" strokeWidth="6" strokeLinecap="round" />
                                            <circle
                                                cx="50" cy="50" r="40"
                                                fill="none"
                                                stroke="#eec78e"
                                                strokeWidth="6"
                                                strokeLinecap="round"
                                                strokeDasharray="251.2"
                                                strokeDashoffset="0"
                                                className="animate-[draw-circle_2s_ease-out_forwards]"
                                            />
                                        </svg>
                                        <div className="absolute inset-0 flex items-center justify-center text-prestige-300">
                                            <Activity size={32} strokeWidth={2} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="w-full bg-surface border-t border-surface-divider py-20">
                <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-4 gap-12 text-sm">
                    <div className="col-span-1">
                        <div className="flex items-center gap-2 mb-6">
                            <div className="w-8 h-8 bg-brand-900 rounded-lg flex items-center justify-center text-white font-bold text-lg shadow-sm font-serif">M</div>
                            <span className="text-xl font-bold tracking-tight text-brand-900">Minutes AI</span>
                        </div>
                        <p className="text-ink-secondary leading-relaxed mb-6">
                            {t.landing.footerDesc}
                        </p>
                        <div className="text-ink-muted">© 2024 Minutes AI Inc.</div>
                    </div>

                    <div className="flex flex-col gap-4">
                        <h4 className="font-bold text-brand-900">{language === 'en' ? 'Product' : '产品'}</h4>
                        <button onClick={() => onNavigate?.('landing')} className="text-left text-ink-secondary hover:text-brand-900 transition-colors">{t.nav.features}</button>
                        <button onClick={() => onNavigate?.('pricing')} className="text-left text-ink-secondary hover:text-brand-900 transition-colors">{t.nav.pricing}</button>
                        <button onClick={() => onNavigate?.('enterprise')} className="text-left text-ink-secondary hover:text-brand-900 transition-colors">{language === 'en' ? 'Enterprise' : '企业版'}</button>
                        <button onClick={() => onNavigate?.('api_page')} className="text-left text-ink-secondary hover:text-brand-900 transition-colors">API</button>
                    </div>

                    <div className="flex flex-col gap-4">
                        <h4 className="font-bold text-brand-900">{language === 'en' ? 'Company' : '公司'}</h4>
                        <button onClick={() => onNavigate?.('about')} className="text-left text-ink-secondary hover:text-brand-900 transition-colors">{language === 'en' ? 'About' : '关于我们'}</button>
                        <button onClick={() => onNavigate?.('blog')} className="text-left text-ink-secondary hover:text-brand-900 transition-colors">{language === 'en' ? 'Blog' : '博客'}</button>
                        <button onClick={() => onNavigate?.('careers')} className="text-left text-ink-secondary hover:text-brand-900 transition-colors">{language === 'en' ? 'Careers' : '加入我们'}</button>
                        <button onClick={() => onNavigate?.('contact')} className="text-left text-ink-secondary hover:text-brand-900 transition-colors">{language === 'en' ? 'Contact' : '联系我们'}</button>
                    </div>

                    <div className="flex flex-col gap-4">
                        <h4 className="font-bold text-brand-900">{language === 'en' ? 'Legal' : '合规'}</h4>
                        <button onClick={() => onNavigate?.('privacy')} className="text-left text-ink-secondary hover:text-brand-900 transition-colors">{language === 'en' ? 'Privacy' : '隐私协议'}</button>
                        <button onClick={() => onNavigate?.('terms')} className="text-left text-ink-secondary hover:text-brand-900 transition-colors">{language === 'en' ? 'Terms' : '服务条款'}</button>
                        <button onClick={() => onNavigate?.('security_page')} className="text-left text-ink-secondary hover:text-brand-900 transition-colors">{language === 'en' ? 'Security' : '安全合规'}</button>
                    </div>
                </div>
            </footer>
        </div>
    );
}
